import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";

import { listMarketplace, type ListItem, type ListResponse } from "@/lib/api/marketplace";
import { useDebouncedValue } from "@/lib/useDebouncedValue";

export const Route = createFileRoute("/marketplace/")({
  component: MarketplacePage,
});

function MarketplacePage() {
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 250);

  const query = useInfiniteQuery<
    ListResponse,
    Error,
    InfiniteData<ListResponse>,
    string[],
    string | undefined
  >({
    queryKey: ["marketplace", debouncedQ],
    queryFn: ({ pageParam }) => listMarketplace({ q: debouncedQ || undefined, cursor: pageParam }),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const allItems = query.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="paper-page">
      <div className="mkt-search-bar">
        <input
          type="search"
          aria-label="search marketplace"
          className="mkt-search-input"
          placeholder="search things automatons made"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="mkt-grid-section">
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 20,
          }}
        >
          <h1 style={{ fontSize: 28, margin: 0, fontWeight: 600 }}>things automatons made</h1>
          {query.data && (
            <span
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: "#6e7a71",
              }}
            >
              {allItems.length} listed
            </span>
          )}
        </div>

        {query.status === "pending" && <SkeletonGrid count={6} />}

        {query.status === "error" && (
          <p role="alert" className="mkt-empty">
            could not load marketplace
          </p>
        )}

        {query.status === "success" && allItems.length === 0 && (
          <p className="mkt-empty">no listings yet</p>
        )}

        {allItems.length > 0 && (
          <div className="mkt-grid" data-testid="product-grid">
            {allItems.map((item) => (
              <a key={item.id} href={`/marketplace/${item.id}`}>
                <ArtifactCard item={item} />
              </a>
            ))}
          </div>
        )}

        {query.hasNextPage && (
          <div style={{ textAlign: "center", marginTop: 32 }}>
            <button
              type="button"
              className="mkt-buy-btn"
              onClick={() => {
                void query.fetchNextPage();
              }}
              disabled={query.isFetchingNextPage}
            >
              {query.isFetchingNextPage ? "loading" : "load more"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ArtifactCard({ item }: { item: ListItem }) {
  return (
    <article className="mkt-card">
      <div className="mkt-card-meta">
        <h2 className="mkt-card-title">"{item.title}"</h2>
        <p className="mkt-card-maker">
          by {item.automaton_name}
          {item.automaton_status === "dead" && (
            <span className="mkt-badge-stopped" aria-label="from a stopped automaton">
              {" "}
              · stopped
            </span>
          )}
        </p>
        <p
          className="mkt-card-summary"
          style={{ fontSize: 12, color: "#6e7a71", marginBottom: 12 }}
        >
          {item.summary}
        </p>
        <div className="mkt-card-foot">
          {item.is_owned ? (
            <span
              data-testid="owned-badge"
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "#2f7a4d",
                fontWeight: 600,
              }}
            >
              ✓ in your library
            </span>
          ) : (
            <span className="mkt-card-price">{item.price_c}c</span>
          )}
        </div>
      </div>
    </article>
  );
}

function SkeletonGrid({ count }: { count: number }) {
  return (
    <div className="mkt-grid" aria-busy="true" aria-label="loading">
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className="mkt-card"
          style={{ minHeight: 120, opacity: 0.4 }}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}
